// @feature-spec brain/vidmax/feature-specs/admin-ad-engine-formats.md
/**
 * `/admin/ad-engine/formats` — the format catalogue.
 *
 * Flat route file for the same reason as `ad-engine.index.tsx`: a `ad-engine.tsx` parent would
 * mask the detail route. A static segment beats the dynamic `$videoId`, so `/formats` lands here
 * and not on a video called "formats".
 *
 * Owns the query and the navigation; `FormatCatalog` stays a controlled view.
 */
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, AixContentHeader, Button } from "@aix/ui"
import { listAdFormatDrafts } from "@aix/vidmax-shared/src/adEngine/formats/referenceDrafts"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"

import { FormatCatalog } from "~/components/AdEngine/FormatCatalog"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { api } from "~/utils/api"

function AdEngineFormatsPage() {
  const search = Route.useSearch()
  const { focus } = search
  const navigate = useNavigate()
  const formatsQuery = api.adEngine.formats.useQuery()
  const entries = formatsQuery.data ?? []

  return (
    <SuperAdminLayout>
      <CustomHead title="Ad Engine formats" />
      <div className="container mx-auto space-y-6 pb-12">
        <AixContentHeader className="px-4 pt-8 sm:px-8">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">Ad formats</h1>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Browse the inspiration. Open a format to see how it works.
              </p>
            </div>
            <Button size="sm" variant="outline" asChild>
              <Link to="/admin/ad-engine">← All ad projects</Link>
            </Button>
          </div>
        </AixContentHeader>

        <div className="space-y-6 px-4 sm:px-8">
          <FormatCatalog
            entries={entries}
            drafts={listAdFormatDrafts()}
            errorMessage={formatsQuery.error ? "The registry request failed." : undefined}
            isLoading={formatsQuery.isLoading}
            query={search.q ?? ""}
            category={search.category ?? "all"}
            status={search.status ?? "all"}
            {...(focus ? { focusId: focus } : {})}
            onFiltersChange={({ query, category, status }) =>
              void navigate({
                to: "/admin/ad-engine/formats",
                search: {
                  ...search,
                  q: query || undefined,
                  category: category === "all" ? undefined : category,
                  status: status === "all" ? undefined : status,
                },
                replace: true,
                resetScroll: false,
              })
            }
            onSelectFormat={(formatId) =>
              void navigate({
                to: "/admin/ad-engine/formats",
                search: { ...search, focus: formatId },
              })
            }
            onOpenProjects={(formatId) => void navigate({ to: "/admin/ad-engine", search: { format: formatId } })}
          />
          <Accordion type="single" collapsible className="border-t">
            <AccordionItem value="format-help" className="border-b-0">
              <AccordionTrigger className="text-sm">How formats work</AccordionTrigger>
              <AccordionContent className="max-w-3xl space-y-3 text-sm leading-relaxed text-muted-foreground">
                <p>
                  A format is a reusable ad recipe. The concept describes its visual style; the pattern describes the
                  order of its story beats. Several reference ads can illustrate the same recipe.
                </p>
                <p>
                  Reference-only formats are extracted inspiration, not generation-ready templates. Runnable formats are
                  supported by the engine, but that does not mean their output quality has been verified.
                </p>
                <p>
                  Open a format for its overview, timed breakdown, and technical details. New recipes are reviewed in
                  code before they become runnable.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </SuperAdminLayout>
  )
}

interface IAdEngineFormatsSearch {
  /** Focused detail on this route; existing fleet links remain valid. */
  focus?: string
  q?: string
  category?: string
  status?: "reference" | "runnable"
}

export const Route = createFileRoute("/admin/ad-engine/formats")({
  component: AdEngineFormatsPage,
  validateSearch: (search: Record<string, unknown>): IAdEngineFormatsSearch => ({
    ...(typeof search.focus === "string" && search.focus ? { focus: search.focus } : {}),
    ...(typeof search.q === "string" && search.q ? { q: search.q } : {}),
    ...(typeof search.category === "string" && search.category ? { category: search.category } : {}),
    ...(search.status === "reference" || search.status === "runnable" ? { status: search.status } : {}),
  }),
})
